import { Building2, Cog, Palette, Save, Settings, Shield } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useTenant } from "@/contexts/TenantContext";

export function SettingsPage() {
	const { tenant, updateTenant } = useTenant();
	const [isSaving, setIsSaving] = useState(false);
	const [activeTab, setActiveTab] = useState("general");

	// Form state
	const [formData, setFormData] = useState({
		name: tenant?.name || "",
		companyName: tenant?.companyName || "",
		website: tenant?.website || "",
		description: tenant?.description || "",
		customDomain: tenant?.settings?.customDomain || "",
		allowPublicPackages: tenant?.settings?.allowPublicPackages ?? true,
	});

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!tenant) return;

		try {
			setIsSaving(true);
			await updateTenant({
				name: formData.name,
				companyName: formData.companyName,
				website: formData.website,
				description: formData.description,
				settings: {
					customDomain: formData.customDomain,
					allowPublicPackages: formData.allowPublicPackages,
					webhookSettings: tenant.settings?.webhookSettings || {
						enabled: false,
						endpoints: [],
					},
					securitySettings: tenant.settings?.securitySettings || {
						enforceSSO: false,
						allowedDomains: [],
					},
				},
			});
		} catch (error) {
			console.error("Failed to update settings:", error);
		} finally {
			setIsSaving(false);
		}
	};

	const tabs = [
		{ id: "general", name: "General", icon: Building2 },
		{ id: "security", name: "Security", icon: Shield },
		{ id: "branding", name: "Branding", icon: Palette },
		{ id: "advanced", name: "Advanced", icon: Cog },
	];

	return (
		<div className="space-y-6">
			{/* Header */}
			<div>
				<h1 className="text-2xl font-bold text-gray-900">Settings</h1>
				<p className="text-gray-500 mt-1">
					Configure tenant settings and preferences
				</p>
			</div>

			{/* Tabs */}
			<div className="border-b border-gray-200">
				<nav className="-mb-px flex space-x-8">
					{tabs.map((tab) => {
						const Icon = tab.icon;
						return (
							<button
								key={tab.id}
								onClick={() => setActiveTab(tab.id)}
								className={`
                  flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm
                  ${
										activeTab === tab.id
											? "border-primary text-primary"
											: "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
									}
                `}
							>
								<Icon className="w-5 h-5" />
								<span>{tab.name}</span>
							</button>
						);
					})}
				</nav>
			</div>

			{/* Tab content */}
			<form onSubmit={handleSubmit}>
				{activeTab === "general" && (
					<div className="space-y-6">
						<Card>
							<CardHeader>
								<CardTitle>Organization Information</CardTitle>
								<CardDescription>
									Basic information about your organization
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<div>
										<Label htmlFor="name">Tenant Name</Label>
										<Input
											id="name"
											value={formData.name}
											onChange={(e) =>
												setFormData({ ...formData, name: e.target.value })
											}
											placeholder="My Registry"
										/>
										<p className="text-xs text-gray-500 mt-1">
											Display name for your package registry
										</p>
									</div>
									<div>
										<Label htmlFor="companyName">Company Name</Label>
										<Input
											id="companyName"
											value={formData.companyName}
											onChange={(e) =>
												setFormData({
													...formData,
													companyName: e.target.value,
												})
											}
											placeholder="Acme Corporation"
										/>
									</div>
								</div>

								<div>
									<Label htmlFor="website">Website</Label>
									<Input
										id="website"
										type="url"
										value={formData.website}
										onChange={(e) =>
											setFormData({ ...formData, website: e.target.value })
										}
										placeholder="https://example.com"
									/>
								</div>

								<div>
									<Label htmlFor="description">Description</Label>
									<textarea
										id="description"
										value={formData.description}
										onChange={(e) =>
											setFormData({ ...formData, description: e.target.value })
										}
										placeholder="Tell us about your package registry..."
										rows={3}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
									/>
								</div>
							</CardContent>
						</Card>

						<Card>
							<CardHeader>
								<CardTitle>Registry Settings</CardTitle>
								<CardDescription>
									Configure how your package registry behaves
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								<div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
									<div>
										<h4 className="font-medium text-gray-900">
											Public Packages
										</h4>
										<p className="text-sm text-gray-500">
											Allow publishing packages with public visibility
										</p>
									</div>
									<label className="relative inline-flex items-center cursor-pointer">
										<input
											type="checkbox"
											checked={formData.allowPublicPackages}
											onChange={(e) =>
												setFormData({
													...formData,
													allowPublicPackages: e.target.checked,
												})
											}
											className="sr-only peer"
										/>
										<div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
									</label>
								</div>

								<div>
									<Label htmlFor="customDomain">Custom Domain</Label>
									<Input
										id="customDomain"
										value={formData.customDomain}
										onChange={(e) =>
											setFormData({ ...formData, customDomain: e.target.value })
										}
										placeholder="registry.example.com"
									/>
									<p className="text-xs text-gray-500 mt-1">
										Use your own domain for the package registry (Enterprise
										only)
									</p>
								</div>
							</CardContent>
						</Card>
					</div>
				)}

				{activeTab === "security" && (
					<div className="space-y-6">
						<Card>
							<CardHeader>
								<CardTitle>Authentication & Access</CardTitle>
								<CardDescription>
									Manage authentication and access control settings
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								<div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
									<div>
										<h4 className="font-medium text-gray-900">
											Two-Factor Authentication
										</h4>
										<p className="text-sm text-gray-500">
											Require 2FA for all team members
										</p>
									</div>
									<Button variant="outline">Configure</Button>
								</div>

								<div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
									<div>
										<h4 className="font-medium text-gray-900">
											SSO Integration
										</h4>
										<p className="text-sm text-gray-500">
											Single Sign-On with SAML or OAuth
										</p>
									</div>
									<Button variant="outline">Setup SSO</Button>
								</div>

								<div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
									<div>
										<h4 className="font-medium text-gray-900">IP Allowlist</h4>
										<p className="text-sm text-gray-500">
											Restrict access to specific IP addresses
										</p>
									</div>
									<Button variant="outline">Manage IPs</Button>
								</div>
							</CardContent>
						</Card>

						<Card>
							<CardHeader>
								<CardTitle>Audit Logs</CardTitle>
								<CardDescription>
									Track all security-related events
								</CardDescription>
							</CardHeader>
							<CardContent>
								<div className="text-sm text-gray-600">
									<p>
										Audit logging is available on Team and Enterprise plans.
									</p>
									<Button variant="outline" className="mt-4" asChild>
										<Link to="/dashboard/logs">View Audit Logs</Link>
									</Button>
								</div>
							</CardContent>
						</Card>
					</div>
				)}

				{activeTab === "branding" && (
					<div className="space-y-6">
						<Card>
							<CardHeader>
								<CardTitle>Brand Identity</CardTitle>
								<CardDescription>
									Customize the look and feel of your registry
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								<div>
									<Label>Logo</Label>
									<div className="mt-2 flex items-center space-x-4">
										<div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
											<Settings className="w-8 h-8 text-gray-400" />
										</div>
										<div>
											<Button variant="outline" size="sm">
												Upload Logo
											</Button>
											<p className="text-xs text-gray-500 mt-1">
												PNG or SVG, max 2MB
											</p>
										</div>
									</div>
								</div>

								<div>
									<Label htmlFor="primaryColor">Primary Color</Label>
									<div className="flex items-center space-x-3 mt-2">
										<input
											type="color"
											id="primaryColor"
											defaultValue="#3b82f6"
											className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
										/>
										<Input
											value="#3b82f6"
											placeholder="#3b82f6"
											className="flex-1"
											readOnly
										/>
									</div>
								</div>
							</CardContent>
						</Card>

						<Card>
							<CardHeader>
								<CardTitle>Custom CSS</CardTitle>
								<CardDescription>
									Add custom styles to your registry (Enterprise only)
								</CardDescription>
							</CardHeader>
							<CardContent>
								<textarea
									placeholder="/* Your custom CSS */"
									rows={6}
									className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
								/>
							</CardContent>
						</Card>
					</div>
				)}

				{activeTab === "advanced" && (
					<div className="space-y-6">
						<Card>
							<CardHeader>
								<CardTitle>API Configuration</CardTitle>
								<CardDescription>
									Advanced API and webhook settings
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								<div>
									<Label>Registry URL</Label>
									<Input
										value={`https://${tenant?.id}.artify.monster`}
										readOnly
										className="bg-gray-50"
									/>
								</div>

								<div>
									<Label>API Endpoint</Label>
									<Input
										value={`https://api.artify.monster/t/${tenant?.id}`}
										readOnly
										className="bg-gray-50"
									/>
								</div>
							</CardContent>
						</Card>

						<Card className="border-red-200 bg-red-50">
							<CardHeader>
								<CardTitle className="text-red-900">Danger Zone</CardTitle>
								<CardDescription className="text-red-700">
									Irreversible and destructive actions
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-3">
								<div className="flex items-center justify-between p-4 bg-white border border-red-200 rounded-lg">
									<div>
										<h4 className="font-medium text-gray-900">
											Transfer Ownership
										</h4>
										<p className="text-sm text-gray-500">
											Transfer this tenant to another user
										</p>
									</div>
									<Button
										variant="outline"
										className="border-red-300 text-red-600 hover:bg-red-50"
									>
										Transfer
									</Button>
								</div>

								<div className="flex items-center justify-between p-4 bg-white border border-red-200 rounded-lg">
									<div>
										<h4 className="font-medium text-gray-900">Delete Tenant</h4>
										<p className="text-sm text-gray-500">
											Permanently delete this tenant and all data
										</p>
									</div>
									<Button
										variant="outline"
										className="border-red-300 text-red-600 hover:bg-red-50"
									>
										Delete
									</Button>
								</div>
							</CardContent>
						</Card>
					</div>
				)}

				{/* Save button */}
				<div className="flex justify-end">
					<Button type="submit" disabled={isSaving}>
						{isSaving ? (
							<div className="flex items-center space-x-2">
								<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
								<span>Saving...</span>
							</div>
						) : (
							<>
								<Save className="w-4 h-4 mr-2" />
								Save Changes
							</>
						)}
					</Button>
				</div>
			</form>
		</div>
	);
}
